import { useEffect, useState } from 'react';
import {
  Container, Typography, Box, Button, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Select, Stack, Alert, Snackbar, CircularProgress, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import {
  getTenantUsers, getRoles, inviteUser, assignRole, setUserActive,
  getPendingMembers, approveMember, rejectMember,
  getImpersonationRequests, decideImpersonationRequest,
} from '../../api/saasApi';
import { useMe } from '../../context/MeContext';
import { ConfirmDialog } from '../../components/ui';

const Users = () => {
  const { can, me } = useMe();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [pending, setPending] = useState([]);
  const [impReqs, setImpReqs] = useState([]);
  const [pendingRole, setPendingRole] = useState({});   // userId -> chosen roleId
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState('');
  const [toggleTarget, setToggleTarget] = useState(null); // user awaiting deactivate/reactivate confirm
  const [toggling, setToggling] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active'); // all | active | deactivated

  const canInvite = can('user:invite');
  const canUpdate = can('user:update');
  const canApprove = can('user:approve');
  const canRemove = can('user:remove');

  const load = async () => {
    try {
      const requests = [getTenantUsers(), getRoles()];
      if (canApprove) requests.push(getPendingMembers());

      const [u, r, p] = await Promise.all(requests);
      setUsers(u.data?.data || []);
      setRoles(r.data?.data || []);
      setPending(p ? p.data?.data || [] : []);

      if (canApprove) {
        try {
          const ir = await getImpersonationRequests();
          setImpReqs(ir.data?.data || []);
        } catch (e2) {
          // Don't hide this: an unseen failure here means support requests silently never appear.
          setError(e2?.response?.data?.messages?.[0] || 'Could not load support access requests.');
        }
      }
    } catch (e) {
      setError('Failed to load users.');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [canApprove]);

  const decide = async (userId, action) => {
    setBusyId(userId);
    setError('');
    try {
      const res = action === 'approve'
        ? await approveMember(userId, pendingRole[userId])
        : await rejectMember(userId, null);

      if (res.data?.succeeded === false) {
        setError(res.data.messages?.[0] || 'Could not complete that.');
        return;
      }
      setToast(action === 'approve' ? 'Clinician approved.' : 'Request declined.');
      await load();
    } catch (e) {
      setError(e?.response?.data?.messages?.[0] || 'Could not complete that.');
    } finally {
      setBusyId(null);
    }
  };

  const decideImp = async (requestId, approve) => {
    setBusyId('imp-' + requestId);
    setError('');
    try {
      const res = await decideImpersonationRequest(requestId, approve);
      if (res.data?.succeeded === false) {
        setError(res.data.messages?.[0] || 'Could not complete that.');
        return;
      }
      setToast(approve ? 'Support access approved (60 min).' : 'Request denied.');
      await load();
    } catch (e) {
      setError(e?.response?.data?.messages?.[0] || 'Could not complete that.');
    } finally {
      setBusyId(null);
    }
  };

  const submitInvite = async () => {
    try {
      const res = await inviteUser(email, roleId);
      if (res.data?.succeeded === false) { setError(res.data.messages?.[0] || 'Invite failed'); return; }
      setToast('Invitation created.');
      setInviteOpen(false); setEmail(''); setRoleId('');
    } catch (e) {
      setError(e?.response?.data?.messages?.[0] || 'Invite failed (seat limit?).');
    }
  };

  const changeRole = async (userId, newRoleId) => {
    try {
      const res = await assignRole(userId, newRoleId);
      if (res.data?.succeeded === false) {
        setError(res.data.messages?.[0] || 'Could not update role.');
        return;
      }
      // Reflect the change immediately so the row and picker stay in sync.
      setUsers((list) => list.map((u) => (
        u.id === userId
          ? { ...u, roleId: newRoleId, roleName: roles.find((r) => r.id === newRoleId)?.name ?? u.roleName }
          : u
      )));
      setToast('Role updated.');
    } catch (e) {
      setError(e?.response?.data?.messages?.[0] || 'Could not update role.');
    }
  };

  const confirmToggle = async () => {
    const target = toggleTarget;
    if (!target) return;
    const nextActive = !target.isActive;
    setToggling(true);
    setError('');
    try {
      const res = await setUserActive(target.id, nextActive);
      if (res.data?.succeeded === false) {
        setError(res.data.messages?.[0] || 'Could not update that clinician.');
        return;
      }
      setUsers((list) => list.map((u) => (u.id === target.id ? { ...u, isActive: nextActive } : u)));
      setToast(nextActive ? 'Clinician reactivated.' : 'Clinician deactivated.');
    } catch (e) {
      setError(e?.response?.data?.messages?.[0] || 'Could not update that clinician.');
    } finally {
      setToggling(false);
      setToggleTarget(null);
    }
  };

  const activeCount = users.filter((u) => u.isActive).length;
  const deactivatedCount = users.length - activeCount;
  const visibleUsers = users.filter((u) => (
    statusFilter === 'all' ? true : statusFilter === 'active' ? u.isActive : !u.isActive
  ));

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>Clinicians</Typography>
        {canInvite && <Button variant="contained" onClick={() => setInviteOpen(true)}>Invite clinician</Button>}
      </Stack>
      {error && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Join requests: people who used this clinic's code and are waiting.
          They have no access at all until approved here. */}
      {canApprove && pending.length > 0 && (
        <Paper sx={{ mb: 3, borderLeft: 4, borderColor: 'warning.main' }}>
          <Box sx={{ px: 2, pt: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              Requests to join ({pending.length})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              These people used your clinic code. They can&apos;t see any patients until you approve them.
            </Typography>
          </Box>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Requested</TableCell>
                <TableCell>Role to grant</TableCell>
                <TableCell align="right">Decision</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pending.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>
                    {p.email}
                    {!p.isEmailVerified && (
                      <Typography variant="caption" color="warning.main" display="block">
                        email not verified
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {p.requestedAt ? new Date(p.requestedAt).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small" displayEmpty sx={{ minWidth: 160 }}
                      value={pendingRole[p.id] || ''}
                      onChange={(e) => setPendingRole((s) => ({ ...s, [p.id]: e.target.value }))}
                    >
                      <MenuItem value="" disabled>Choose role…</MenuItem>
                      {roles.map((r) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                    </Select>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small" variant="contained"
                        disabled={!pendingRole[p.id] || busyId === p.id}
                        onClick={() => decide(p.id, 'approve')}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small" color="inherit"
                        disabled={busyId === p.id}
                        onClick={() => decide(p.id, 'reject')}
                      >
                        Decline
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Support-access requests: a Vqura platform admin asking to enter this
          clinic to help. They get in only if you approve, for 60 minutes, and
          it is written to the audit log. */}
      {canApprove && impReqs.length > 0 && (
        <Paper sx={{ mb: 3, borderLeft: 4, borderColor: 'info.main' }}>
          <Box sx={{ px: 2, pt: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              Support access requests ({impReqs.length})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              A Vqura platform admin is asking to enter your clinic to help. Access is granted only if you approve, lasts 60 minutes, and is logged.
            </Typography>
          </Box>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Requested by</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Requested</TableCell>
                <TableCell align="right">Decision</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {impReqs.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    {r.requestedByName || '—'}
                    <Typography variant="caption" color="text.secondary" display="block">
                      {r.requestedByEmail}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {r.reason || <Typography variant="caption" color="text.secondary">no reason given</Typography>}
                  </TableCell>
                  <TableCell>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small" variant="contained"
                        disabled={busyId === 'imp-' + r.id}
                        onClick={() => decideImp(r.id, true)}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small" color="inherit"
                        disabled={busyId === 'imp-' + r.id}
                        onClick={() => decideImp(r.id, false)}
                      >
                        Deny
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Stack direction="row" justifyContent="flex-end" mb={1.5}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFilter}
          onChange={(_, v) => { if (v) setStatusFilter(v); }}
          aria-label="Filter clinicians by status"
        >
          <ToggleButton value="all">All ({users.length})</ToggleButton>
          <ToggleButton value="active">Active ({activeCount})</ToggleButton>
          <ToggleButton value="deactivated">Deactivated ({deactivatedCount})</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Assign role</TableCell>
              {canRemove && <TableCell align="right">Access</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleUsers.map((u) => (
              <TableRow key={u.id}>
                <TableCell>{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  <Typography variant="body2" color={u.isActive ? 'text.primary' : 'error.main'} fontWeight={u.isActive ? 400 : 600}>
                    {u.isActive ? 'Active' : 'Deactivated'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {u.roleName
                    ? u.roleName
                    : <Typography variant="caption" color="text.secondary">No role</Typography>}
                </TableCell>
                <TableCell>
                  <Select size="small" displayEmpty value={u.roleId ?? ''} disabled={!canUpdate}
                          onChange={(e) => changeRole(u.id, e.target.value)} sx={{ minWidth: 160 }}>
                    <MenuItem value="" disabled>Choose role…</MenuItem>
                    {roles.map((r) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                  </Select>
                </TableCell>
                {canRemove && (
                  <TableCell align="right">
                    {/* Your own account and Vqura platform admins can't be switched off here;
                        the API also refuses the clinic owner and explains why. */}
                    {u.id !== me?.userId && !u.isPlatformAdmin && (
                      <Button
                        size="small"
                        color={u.isActive ? 'error' : 'primary'}
                        variant={u.isActive ? 'text' : 'outlined'}
                        onClick={() => setToggleTarget(u)}
                      >
                        {u.isActive ? 'Deactivate' : 'Reactivate'}
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {visibleUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={canRemove ? 6 : 5}>
                  {users.length === 0
                    ? 'No users yet.'
                    : statusFilter === 'active' ? 'No active clinicians.' : 'No deactivated clinicians.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Invite clinician</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth type="email" label="Email" margin="normal" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Select fullWidth displayEmpty value={roleId} onChange={(e) => setRoleId(e.target.value)} sx={{ mt: 1 }}>
            <MenuItem value="" disabled>Select role…</MenuItem>
            {roles.map((r) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!email || !roleId} onClick={submitInvite}>Send invite</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!toggleTarget}
        title={toggleTarget?.isActive ? `Deactivate ${toggleTarget?.name || 'this clinician'}?` : `Reactivate ${toggleTarget?.name || 'this clinician'}?`}
        body={toggleTarget?.isActive
          ? 'They will be signed out and unable to sign in until reactivated. Their patients, assessments and reports stay in the clinic.'
          : 'They will be able to sign in again with their existing role.'}
        confirmLabel={toggleTarget?.isActive ? 'Deactivate' : 'Reactivate'}
        destructive={!!toggleTarget?.isActive}
        busy={toggling}
        onConfirm={confirmToggle}
        onClose={() => setToggleTarget(null)}
      />

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} />
    </Container>
  );
};

export default Users;
